<template>
  <div data-testid="grace-periods-table">
    <data-table
      ref="datatableRef"
      test-id-prefix="grace-periods"
      :config="tableConfig"
      :data="rows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @button-click="handleButtonClick"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Grace_PeriodRow } from '../../../contracts/Grace_PeriodRow'

const { t } = useI18n()

interface DisplayRow extends Grace_PeriodRow {
  starts_at_display?: string
  ends_at_display?: string
}

interface Props {
  rows: DisplayRow[]
}

defineProps<Props>()

const emit = defineEmits<{
  revoke: [row: Grace_PeriodRow]
  refresh: []
}>()

interface DataTableExposed {
  loadData: () => void
}

const datatableRef = ref<DataTableExposed | null>(null)

const sourceBadgeConfig = (row: Record<string, unknown>) => {
  const source = row.source as string | undefined
  const map: Record<string, { label: string; severity: string }> = {
    staff:           { label: t('Staff'),        severity: 'info' },
    payment_overdue: { label: t('Inadimplência'), severity: 'warn' },
    backfill:        { label: t('Migração'),      severity: 'secondary' },
  }
  const cfg = source ? (map[source] ?? { label: source, severity: 'secondary' }) : { label: '-', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

const situacaoBadgeConfig = (row: Record<string, unknown>) => {
  if (row.is_live === true) {
    return { label: t('Vigente'), severity: 'success', tooltip: t('Vigente') }
  }
  if (row.revoked_at != null) {
    return { label: t('Revogado'), severity: 'danger', tooltip: t('Revogado') }
  }
  return { label: t('Expirado'), severity: 'secondary', tooltip: t('Expirado') }
}

const tableConfig = computed(() => ({
  tableId: 'tabelaGracePeriods',
  title: t('Períodos de Carência'),
  columns: [
    {
      data: 'franchise_name',
      title: t('Franquia'),
      sortable: true,
    },
    {
      data: 'source',
      title: t('Origem'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: sourceBadgeConfig,
    },
    {
      data: 'starts_at_display',
      title: t('Início'),
      sortable: false,
    },
    {
      data: 'ends_at_display',
      title: t('Fim'),
      sortable: false,
    },
    {
      data: 'days_remaining',
      title: t('Dias restantes'),
      sortable: true,
    },
    {
      data: 'granted_by',
      title: t('Concedido por'),
      sortable: true,
    },
    {
      data: 'reason',
      title: t('Motivo'),
      sortable: false,
    },
    {
      data: 'grace_id',
      title: t('Situação'),
      sortable: false,
      renderType: 'badge',
      badgeConfig: situacaoBadgeConfig,
    },
    {
      data: 'grace_id',
      title: t('Revogar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Revogar'),
      buttonIcon: 'pi pi-times',
      buttonAction: 'revoke',
      buttonTestId: (row: Record<string, unknown>) =>
        `grace-periods-revoke-row-${row.grace_id as number}`,
      buttonCondition: (row: Record<string, unknown>) => row.is_live === true,
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) => {
    const id = row?.grace_id
    return id == null ? '' : `grace-periods-row-${id as number}`
  },
}))

function handleButtonClick(e: { value: unknown; data: Grace_PeriodRow; action: string }): void {
  if (e.action === 'revoke') {
    emit('revoke', e.data)
  }
}
</script>
